import { StatusCodes } from 'http-status-codes';
import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUserRepository } from '../user/useUserRepository';
import { SocialAuthShell } from './social-auth-shell';
import { SocialAuthSuccess } from './social-auth-success';
import { useSocialMode } from './social-mode-context';
import { startSocialAuthorization } from './start-social-authorization';
import { useSocialConsumerRepository } from './use-social-consumer-repository';

function PasswordToggleIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      focusable="false"
      height="20"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="20"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function createConsumerId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : ((random & 0x3) | 0x8);

    return value.toString(16);
  });
}

function readErrorMessage(error: unknown): string {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return 'No hemos podido crear la cuenta. Inténtalo de nuevo.';
  }

  const response = error.response;
  if (typeof response !== 'object' || response === null || !('data' in response)) {
    return 'No hemos podido crear la cuenta. Inténtalo de nuevo.';
  }

  const data = response.data;
  const message = typeof data === 'object' && data !== null && 'message' in data && typeof data.message === 'string'
    ? data.message
    : typeof data === 'string'
      ? data
      : '';

  if (
    message === 'The user exist'
    || message.includes('Ya existe una cuenta con este correo')
  ) {
    return 'Ya existe una cuenta con este correo. Inicia sesión o recupera la contraseña.';
  }

  if (message) {
    return message;
  }

  return 'No hemos podido crear la cuenta. Inténtalo de nuevo.';
}

export function SocialRegister() {
  const { createConsumer } = useSocialConsumerRepository();
  const { resendVerificationEmail } = useUserRepository();
  const { isSocialMode, intent, withSocialParam } = useSocialMode();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  useEffect(() => {
    if (!isSocialMode || !intent) return;
    const params = new URLSearchParams(window.location.search);
    if (!['state', 'code_challenge', 'callback', 'attempt_id'].every((key) => params.has(key))) {
      startSocialAuthorization('register', intent);
    }
  }, [isSocialMode, intent]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const fullName = formData.get('fullName');
    const email = formData.get('email');
    const password = formData.get('password');
    const terms = formData.get('terms');

    if (typeof fullName !== 'string' || typeof email !== 'string' || typeof password !== 'string') {
      setError('Completa el nombre, el correo y la contraseña.');
      return;
    }

    if (!terms) {
      setError('Debes aceptar los términos y la política de privacidad.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const response = await createConsumer({
        id: createConsumerId(),
        fullName: fullName.trim(),
        username: email.trim(),
        password,
      });

      if (response.status !== StatusCodes.CREATED) {
        setError('No hemos podido crear la cuenta. Inténtalo de nuevo.');
        return;
      }

      setSentEmail(email.trim());
    } catch (submitError) {
      setError(readErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResend() {
    if (!sentEmail) {
      return;
    }

    setError('');
    setIsResending(true);

    try {
      const sent = await resendVerificationEmail(sentEmail);

      if (!sent) {
        setError('No hemos podido reenviar el correo. Inténtalo de nuevo.');
      }
    } catch {
      setError('No hemos podido reenviar el correo. Inténtalo de nuevo.');
    } finally {
      setIsResending(false);
    }
  }

  return (
    <SocialAuthShell
      claim="Construye tu reputación, referencia a referencia."
      description="Únete a la red social donde la confianza se gana con evidencia pública y mejora con feedback privado."
      footer={sentEmail ? (
        <>
          <p>¿No te ha llegado? <button className="or-social-auth__inline-action" disabled={isResending} onClick={handleResend} type="button">{isResending ? 'Reenviando…' : 'Reenviar'}</button></p>
          <p><Link to={withSocialParam('/login')}>Volver a acceso</Link></p>
        </>
      ) : (
        <p>¿Ya tienes cuenta? <Link to={withSocialParam('/login')}>Iniciar sesión</Link></p>
      )}
      highlights={['Perfil público indexable y compartible.', 'Posiciónate en el ranking por méritos reales.']}
      subtitle={sentEmail ? '' : 'Te llevará menos de un minuto.'}
      title={sentEmail ? '' : 'Crea tu cuenta'}
      variant="register"
    >
      {isSocialMode && intent ? <p role="status">Inicia sesión para terminar tu referencia.</p> : null}
      {sentEmail ? (
        <SocialAuthSuccess title="Revisa tu correo">
          Te hemos enviado un enlace a <strong>{sentEmail}</strong> para confirmar tu cuenta.
        </SocialAuthSuccess>
      ) : (
        <form className="or-social-auth__form" onSubmit={handleSubmit}>
          <div className="or-social-auth__field">
            <label className="or-social-auth__field-label" htmlFor="social-register-fullname">Nombre completo</label>
            <input autoComplete="name" className="or-social-auth__input" id="social-register-fullname" name="fullName" placeholder="Tu nombre" required type="text" />
          </div>
          <div className="or-social-auth__field">
            <label className="or-social-auth__field-label" htmlFor="social-register-email">Correo electrónico</label>
            <input autoComplete="email" className="or-social-auth__input" id="social-register-email" inputMode="email" name="email" placeholder="tucorreo@dominio.com" required type="email" />
          </div>
          <div className="or-social-auth__field">
            <label className="or-social-auth__field-label" htmlFor="social-register-password">Contraseña</label>
            <span className="or-social-auth__password">
              <input autoComplete="new-password" className="or-social-auth__input or-social-auth__input--with-icon" id="social-register-password" minLength={8} name="password" placeholder="Mínimo 8 caracteres" required type={showPassword ? 'text' : 'password'} />
              <button aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'} aria-pressed={showPassword} className="or-social-auth__password-toggle" onClick={() => setShowPassword((visible) => !visible)} type="button">
                <PasswordToggleIcon />
              </button>
            </span>
            <p className="or-social-auth__field-hint">Usa al menos 8 caracteres con letras y números.</p>
          </div>
          <label className="or-social-auth__terms">
            <input className="or-social-auth__checkbox" name="terms" required type="checkbox" />
            <span className="or-social-auth__terms-text">
              Acepto los <a href="https://openreferences.com/aviso-legal/" rel="noreferrer" target="_blank">Términos</a> y la <a href="https://openreferences.com/politica-de-privacidad/" rel="noreferrer" target="_blank">Política de privacidad</a>
            </span>
          </label>
          {error ? <p className="or-social-auth__error" role="alert">{error}</p> : null}
          <button className="or-social-auth__submit" disabled={isSubmitting} type="submit">{isSubmitting ? 'Creando cuenta…' : 'Crear cuenta y continuar'}</button>
        </form>
      )}
    </SocialAuthShell>
  );
}
